/** Protect only a game surface from native dragging, selection and callouts.
 * Feed scrolling stays native unless the caller owns this touch sequence. Browser
 * zoom remains available outside the play surface; OS edge gestures may still win.
 * Blur, scroll and viewport changes retire captured input through its owner. */
export function guardGameSurface(
  surface,
  { ownsTouch = () => true, cancel = () => {}, defaults = true, listen: register } = {},
) {
  const listeners = [];
  const listen = (target, name, handler, options) => {
    if (!target?.addEventListener) return;
    if (register) register(target, name, handler, options);
    else {
      target.addEventListener(name, handler, options);
      listeners.push([target, name, handler, options]);
    }
  };
  const prevent = (event) => {
    if (
      event.target?.closest?.('input, select, textarea, [contenteditable="true"], [role="slider"]')
    )
      return;
    if (event.cancelable !== false) event.preventDefault();
  };
  if (defaults)
    for (const name of ['dragstart', 'selectstart', 'contextmenu']) listen(surface, name, prevent);
  for (const name of ['touchmove', 'gesturestart', 'gesturechange'])
    listen(
      surface,
      name,
      (event) => {
        if (ownsTouch(event)) prevent(event);
      },
      { passive: false },
    );
  const view = surface.ownerDocument?.defaultView ?? globalThis.window;
  for (const name of ['blur', 'resize', 'orientationchange']) listen(view, name, cancel);
  listen(view, 'scroll', cancel, { capture: true, passive: true });
  const style = surface.style;
  const values = {
    userSelect: 'none',
    webkitUserSelect: 'none',
    webkitTouchCallout: 'none',
    overscrollBehavior: 'contain',
  };
  const previous = Object.fromEntries(Object.keys(values).map((key) => [key, style?.[key] ?? '']));
  if (style) Object.assign(style, values);
  return () => {
    for (const [target, name, handler, options] of listeners)
      target?.removeEventListener?.(name, handler, options);
    if (style) Object.assign(style, previous);
  };
}
